import React from "react";
import { IntlProvider } from "react-intl";
import message_en from "utils/i18n/en-US.json";
import message_cn from "utils/i18n/zh-CN.json";
import message_tc from "utils/i18n/zh-TW.json";
import message_ja from "utils/i18n/ja-JP.json";
import { LocaleMapping } from "utils";

interface LayoutType {
  layout: number;
  locale: string;
  step: number;
  structure: {
    footer: boolean;
    header: boolean;
    sideMenu: boolean;
  };
  setLayout: (number: any) => void;
  setLocale: (string: any) => void;
  setStep: (number: any) => void;
}

const defaultLayoutValue = {
  layout: 0,
  locale: "en",
  structure: {
    footer: false,
    header: false,
    sideMenu: false,
  },
  setLayout: (number: any) => {},
  setLocale: (string: any) => {},
  setStep: (number: any) => {},
  step: 0,
};

const messages = {
  "en-US": message_en,
  "zh-TW": message_tc,
  "zh-CN": message_cn,
  "ja-JP": message_ja,
};

const LayoutContext = React.createContext<LayoutType>(defaultLayoutValue);

const LayoutProvider = ({ children }) => {
  const [layout, setLayout] = React.useState(0);
  const [locale, setLocale] = React.useState("en");
  const [structure, setStructure] = React.useState({
    footer: true,
    header: false,
    sideMenu: false,
  });

  const [step, setStep] = React.useState(0);

  const changeLayout = (number: number) => {
    switch (number) {
      case 0:
        setStructure({ footer: true, header: false, sideMenu: false });
        break;
      case 1:
        setStructure({ footer: false, header: true, sideMenu: true });
        break;
      default:
        setStructure({ footer: false, header: false, sideMenu: false });
    }
    setLayout(number);
  };
  const changeLocale = (locale) => {
    setLocale(locale);
  };

  const changeStep = (currentStep) => {
    setStep(currentStep);
  };

  let intlLocale = LocaleMapping[locale];

  return (
    <LayoutContext.Provider
      value={{
        layout,
        locale,
        structure,
        setLayout: changeLayout,
        setLocale: changeLocale,
        setStep: changeStep,
        step,
      }}
    >
      <IntlProvider
        locale={intlLocale}
        key={intlLocale}
        defaultLocale="en-US"
        messages={messages[intlLocale]}
      >
        {children}
      </IntlProvider>
    </LayoutContext.Provider>
  );
};

const useLayout = () => {
  const { layout } = React.useContext(LayoutContext);
  return layout;
};

const useStructure = () => {
  const { structure } = React.useContext(LayoutContext);
  return structure;
};

const useLocale = () => {
  const { locale } = React.useContext(LayoutContext);
  return locale;
};

const useLayoutState = () => {
  const { setLayout } = React.useContext(LayoutContext);
  return setLayout;
};

const useLocaleState = () => {
  const { setLocale } = React.useContext(LayoutContext);
  return setLocale;
};

const useStepState = () => {
  const { setStep } = React.useContext(LayoutContext);
  return setStep;
};

const useStep = () => {
  const { step } = React.useContext(LayoutContext);
  return step;
};

const usePath = () => {
  const { locale } = React.useContext(LayoutContext);
  const root: string = process.env.REACT_APP_SUB_FOLDER
    ? process.env.REACT_APP_SUB_FOLDER
    : "";
  return `${root}/${locale}`;
};

export {
  LayoutProvider,
  useLayout,
  useStructure,
  useLocale,
  useLayoutState as setLayoutState,
  useLocaleState as setLocaleState,
  usePath,
  useStepState as setStepState,
  useStep,
};
